import { Trip, Traveller, Apartment, TripStatistics } from '@/types';
import { regenerateIds } from '@/lib/utils';

/**
 * Calculate statistics for a duplicated trip based on what is being included
 */
export function calculateDuplicationStatistics(
  options: {
    includeApartments: boolean;
    includeTravellers: boolean;
    includeSkiPasses: boolean;
  },
  sourceTrip?: Trip,
  apartments?: Apartment[],
  travellers?: Traveller[]
): TripStatistics {
  const travellerStats = { total: 0, totalSkiClubMembers: 0 };
  const apartmentStats = { total: 0, totalNumberOfBeds: 0, totalOccupancy: 0 };
  const skiPassStats = { total: 0 };

  // Calculate traveller statistics if travellers are included
  if (options.includeTravellers && travellers) {
    travellerStats.total = travellers.length;
    travellerStats.totalSkiClubMembers = travellers.filter(t => t.isSkiClubMember).length;
    
    // Calculate ski pass statistics if ski passes are included
    if (options.includeSkiPasses) {
      skiPassStats.total = travellers.filter(t => t.skiPassType).length;
    }
  }

  // Calculate apartment statistics if apartments are included
  if (options.includeApartments && apartments) {
    apartmentStats.total = apartments.length;
    apartmentStats.totalNumberOfBeds = apartments.reduce((sum, apt) => sum + (apt.maxOccupancy || 0), 0);
    
    // If travellers are not included, reset apartment occupancy to 0
    if (!options.includeTravellers) {
      apartmentStats.totalOccupancy = 0;
    } else {
      apartmentStats.totalOccupancy = apartments.reduce((sum, apt) => sum + (apt.currentOccupancy || 0), 0);
    }
  }

  return {
    travellers: travellerStats,
    apartments: apartmentStats,
    skiPasses: skiPassStats
  };
}

/**
 * Reset apartment occupancy to 0 for all apartments
 */
export function resetApartmentOccupancy(apartments: Apartment[]): Apartment[] {
  return apartments.map(apartment => ({
    ...apartment,
    currentOccupancy: 0
  }));
}

/**
 * Generate new IDs for trip sub-objects and ensure data integrity
 */
export function prepareTripDataForDuplication(
  sourceTrip: Trip,
  options: {
    includeSkiPasses: boolean;
    includeApartmentTypes: boolean;
    includeTransportation: boolean;
  }
) {
  const result = {
    skiPasses: options.includeSkiPasses ? regenerateIds(sourceTrip.skiPasses, 'skipass') : [],
    apartmentTypes: options.includeApartmentTypes ? regenerateIds(sourceTrip.apartmentTypes, 'apt-type') : [],
    transportationOptions: options.includeTransportation ? regenerateIds(sourceTrip.transportationOptions, 'transport') : []
  };

  return result;
}